import type { MultiWatchSources } from '@vueuse/core'

type TargetEndPoints = Extract<
  SiteApiRoutes,
  | '/api/Auxiliares/ByProducto/Ocupaciones'
  | '/api/Auxiliares/ByProducto/Destinos'
  | '/api/Auxiliares/ByProducto/Municipios'
  | '/api/Auxiliares/ByProducto/Nacionalidades'
  | '/api/Auxiliares/ByProducto/Paises'
  | '/api/Auxiliares/ByProducto/Provincias'
  | '/api/Auxiliares/ByProducto/Sectores'
  | '/api/Auxiliares/ByProducto/VehiculoClases'
  | '/api/Auxiliares/ByProducto/VehiculoColores'
  | '/api/Auxiliares/ByProducto/VehiculoMarcas'
  | '/api/Auxiliares/ByProducto/VehiculoModelos'
  | '/api/Auxiliares/ByProducto/VehiculoTipos'
  | '/api/Auxiliares/ByProducto/VehiculoUsos'
  | '/api/Auxiliares/ByProducto/VehiculoVersionesByCatalog'
  | '/api/Auxiliares/ByProducto/VehiculoVersiones'
  | '/api/Auxiliares/ByProducto/AnimalesColores'
  | '/api/Auxiliares/ByProducto/AnimalesRazas'
>

type ArgentoInsuranceProps = {
  emisorId?: number | null
  productoId?: number | null
  brokerProductoId?: number | null
}

type Options = {
  immediate?: boolean
  watch?: false | MultiWatchSources
}

export const useSiteApiDispatcherByProductParams = (
  params: {
    argentoInsuranceProps?: MaybeRef<ArgentoInsuranceProps>
  } = {}
) => {
  const { RemoteConfigurationStore } = stores()

  const _argentoInsuranceProps = computed(() => ({
    emisorId:
      getMaybeRefValue(params.argentoInsuranceProps)?.emisorId ??
      RemoteConfigurationStore.value?.emisor?.id ??
      undefined,
    brokerProductoId:
      getMaybeRefValue(params.argentoInsuranceProps)?.brokerProductoId ??
      RemoteConfigurationStore.value?.brokerProductoId ??
      undefined,
    productoId:
      getMaybeRefValue(params.argentoInsuranceProps)?.productoId ??
      RemoteConfigurationStore.value?.producto?.id ??
      undefined,
  }))

  const argentoInsuranceExtraProps = computed(
    (): SiteApiSchema<'OrigenServicioStringStringDictionaryKeyValuePair'>[] => [
      {
        key: 'Default',
        value: {
          EmisorId: _argentoInsuranceProps.value.emisorId?.toString() ?? '',
          BrokerProductoId: _argentoInsuranceProps.value.brokerProductoId?.toString() ?? '',
        },
      },
    ]
  )

  const builder = <Endpoint extends TargetEndPoints>({
    endpoint,
    body,
    options,
  }: {
    endpoint: Endpoint
    body?: MaybeRef<
      Omit<
        SiteApiRequestBody<Endpoint, SiteApiRoutesMethod<Endpoint>>,
        'items' | 'page' | 'extraProps'
      >
    >
    options?: Options
  }) =>
    [
      endpoint,
      {
        method: 'post' as const,
        body: computed<SiteApiRequestBody<TargetEndPoints, SiteApiRoutesMethod<TargetEndPoints>>>(
          () => ({
            items: -1,
            page: -1,
            extraProps: argentoInsuranceExtraProps.value,
            ...getMaybeRefValue(body),
          })
        ),
        query: computed<SiteApiQuery<TargetEndPoints, SiteApiRoutesMethod<TargetEndPoints>>>(
          () => ({
            productoId: _argentoInsuranceProps.value.productoId,
          })
        ),
        transform(input?: SiteApiRequestResponse<Endpoint, SiteApiRoutesMethod<Endpoint>>) {
          if (input) return input
        },
        key: () => crypto.randomUUID(),
        immediate: options?.immediate ?? false,
        watch: options?.watch ?? false,
      },
    ] as const

  return { builder, argentoInsuranceProps: _argentoInsuranceProps }
}
